import { CommonModule } from '@angular/common'
import { HttpEventType } from '@angular/common/http'
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { Router, RouterLink } from '@angular/router'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions'
import { NzNotificationService } from 'ng-zorro-antd/notification'
import { NzBytesPipe } from 'ng-zorro-antd/pipes'
import { NzProgressStatusType } from 'ng-zorro-antd/progress'
import { NzStatusType } from 'ng-zorro-antd/steps'
import { NzUploadFile } from 'ng-zorro-antd/upload'
import {
  exhaustMap,
  interval,
  last,
  startWith,
  Subscription,
  switchMap,
  takeWhile,
  tap,
} from 'rxjs'
import { filter } from 'rxjs/operators'

import { ButtonComponent } from '../../../components/button/button.component'
import { Branch, ProgressFailureReason } from '../../../models/branch.model'
import { BranchRepository } from '../../../repositories/branch/branch.repository'
import { BrowserTabProgressService } from '../../../services/browser-tab-progress.service'
import { UploadService } from '../../../services/upload.service'
import { ProcessErrorComponent } from '../../upload/process-error/process-error.component'
import {
  END_STATUS,
  EndStatus,
  FAILURE_STATUS,
  FailureStatus,
  MetaDataModel,
  PROCESS_STATUS,
  ProcessStatus,
} from '../upload-file.model'

export interface ProgressUpdateEvent {
  totalProcess: number
  totalProcessStatus: NzStatusType
}

@Component({
  selector: 'app-step-progress',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    NzDescriptionsModule,
    NzBytesPipe,
    RouterLink,
    ButtonComponent,
    ProcessErrorComponent,
  ],
  templateUrl: './step-progress.component.html',
})
export class StepProgressComponent implements OnInit, OnDestroy {
  @Input({ required: true }) public metaDataModel!: MetaDataModel
  @Input({ required: true }) public file!: NzUploadFile
  @Input({ required: true }) public fileLineCount!: number
  @Output() public backToUpload = new EventEmitter<void>()
  @Output() public progressUpdate = new EventEmitter<ProgressUpdateEvent>()

  public readonly PROCESS_STATUS = PROCESS_STATUS

  public fileUploadProcess: number = 0
  public fileUploadStatus: NzProgressStatusType = 'normal'
  public activeProcessStateIndex: number = 0
  public totalProcess: number = 0
  public totalProcessStatus: NzStatusType = 'wait'
  public processFailureReason?: ProgressFailureReason

  private uploadSubscription!: Subscription

  public constructor(
    private readonly notificationService: NzNotificationService,
    private readonly translateService: TranslateService,
    private readonly routerService: Router,
    private readonly uploadService: UploadService,
    private readonly branchRepository: BranchRepository,
    private readonly browserTabProgressService: BrowserTabProgressService
  ) {}

  public ngOnInit(): void {
    queueMicrotask(() => this.createBranch())
  }

  public ngOnDestroy(): void {
    this.uploadSubscription?.unsubscribe()
    this.browserTabProgressService.reset()
  }

  public onBackToUpload(): void {
    this.backToUpload.emit()
  }

  public createBranch(): void {
    this.fileUploadProcess = 0
    this.totalProcess = 0
    this.totalProcessStatus = 'process'
    this.emitProgressUpdate()

    this.uploadSubscription = this.branchRepository
      .create(this.metaDataModel as Partial<Branch>)
      .pipe(
        switchMap((createBranchResponse) => {
          return this.uploadService
            .uploadFile(
              createBranchResponse.signedUrl,
              this.file as unknown as File
            )
            .pipe(
              tap((event) => {
                if (event.type === HttpEventType.Sent) {
                  this.fileUploadProcess = 0
                  this.fileUploadStatus = 'active'
                }
                if (event.type === HttpEventType.UploadProgress) {
                  this.fileUploadProcess = Math.round(
                    (event.loaded / (event.total ?? 0)) * 100
                  )
                  this.updateTotalProcess()
                }
              }),
              filter((event) => {
                return event.type === HttpEventType.Response
              }),
              tap(() => {
                this.fileUploadProcess = 100
                this.fileUploadStatus = 'success'
                this.activeProcessStateIndex = 0
                this.updateTotalProcess()
              }),
              switchMap(() =>
                interval(1000).pipe(
                  startWith(0),
                  exhaustMap(() => {
                    return this.branchRepository.getState(
                      createBranchResponse.branchId
                    )
                  }),
                  tap((res) => {
                    if (
                      PROCESS_STATUS.includes(res.processState as ProcessStatus)
                    ) {
                      this.activeProcessStateIndex =
                        PROCESS_STATUS.indexOf(
                          res.processState as ProcessStatus
                        ) + 1
                      this.updateTotalProcess()
                    }
                  }),
                  takeWhile(
                    (res) =>
                      !END_STATUS.includes(res.processState as EndStatus),
                    true
                  ),
                  last()
                )
              )
            )
        })
      )
      .subscribe({
        next: (res) => {
          if (FAILURE_STATUS.includes(res.processState as FailureStatus)) {
            this.totalProcessStatus = 'error'
            this.processFailureReason = res.processFailureReason
            this.emitProgressUpdate()
          } else {
            this.activeProcessStateIndex = PROCESS_STATUS.length + 1
            this.totalProcessStatus = 'finish'
            this.emitProgressUpdate()

            this.notificationService.success(
              this.translateService.instant(
                'pages.uploadFile.steps.progress.notification.uploadSuccess.title'
              ),
              ''
            )

            this.routerService.navigate(['uploads'])
          }
        },
        error: (err) => {
          console.error('err', err)
          this.totalProcessStatus = 'error'
          this.emitProgressUpdate()
        },
        complete: () => {},
      })
  }

  private updateTotalProcess(): void {
    this.totalProcess =
      (this.fileUploadProcess + 100 * this.activeProcessStateIndex) /
      (PROCESS_STATUS.length + 1)

    this.browserTabProgressService.update(this.totalProcess)
    this.emitProgressUpdate()
  }

  private emitProgressUpdate(): void {
    this.progressUpdate.emit({
      totalProcess: this.totalProcess,
      totalProcessStatus: this.totalProcessStatus,
    })
  }
}
